import React, { ChangeEvent, useState } from "react";
import {
  Avatar,
  Fab,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import { DialogWrapper } from "components/shared/dialog";
import circleButton from "styles/buttons/circle-button.module.scss";
import AddIcon from "@material-ui/icons/Add";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { postTestimonials, uploadContent } from "adapters";
import { useDispatch, useSelector } from "react-redux";
import {
  GetUrlBuilderData,
  SetUrlBuilderData,
  Testimonial,
} from "store/url-builder";
import { useParams } from "react-router-dom";
import { GetConfigData } from "store/config/selectors";
import hoverStyle from "styles/hover-overlay.module.scss";
import { HighlightOffOutlined } from "@material-ui/icons";
import { uploadFile } from "adapters/public-card";
import { da } from "date-fns/locale";

const schema = yup.object().shape({
  rating: yup.number().required(),
  comment: yup.string().required(),
  fullName: yup.string().required(),
  title: yup.string().required(),
});

export interface DlgProps {
  isOpen: any;
  onClose: any;
  testimonial?: Testimonial;
  idx?: number;
  builderType: string;
}

export const TestimonialsDialog = ({
  isOpen,
  onClose,
  testimonial,
  idx,
  builderType,
}: DlgProps) => {
  const confData = useSelector(GetConfigData);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const builderData = useSelector(GetUrlBuilderData);
  const { cardId }: { cardId: string } = useParams();
  const [selectedUserImage, setSelectedUserImage] = useState<File | null>();

  const imageToDisplay = testimonial?.userPhoto
    ? confData.filesBaseUrl +
      confData.filesBasePath +
      "/" +
      testimonial.userPhoto.fileName
    : selectedUserImage
    ? URL.createObjectURL(selectedUserImage)
    : "";

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: testimonial?.title,
      comment: testimonial?.comment,
      rating: testimonial?.rating,
      fullName: testimonial?.fullName,
    },
  });

  const handleImageSelected = (e: ChangeEvent<HTMLInputElement>) =>
    e.target.files !== null && setSelectedUserImage(e.target.files[0]);
  const removeImage = () => {
    setSelectedUserImage(null);
    if (testimonial) testimonial.userPhoto = null;
  };

  const submit = async (formData: Testimonial) => {
    try {
      setLoading(true);

      if (selectedUserImage) {
        // create dummy file
        const _file = new File([""], selectedUserImage.name, {
          type: selectedUserImage.type,
        });

        await uploadContent(_file).then(async ({ data }) => {
          if (!data.success) throw new Error(data.message);

          await uploadFile(cardId, selectedUserImage, data).then(({ data }) => {
            if (!data.success) throw new Error(data.message);

            formData.userPhoto = data.data;
          });
        });
      }

      formData.rating = Number(formData.rating);

      /*
       * Update existing testimonial
       */
      let tempTestimonials = builderData.testimonials;
      if (testimonial && idx !== undefined)
        tempTestimonials[idx] = { ...tempTestimonials[idx], ...formData };
      else tempTestimonials = [...tempTestimonials, formData];

      postTestimonials(cardId, builderType, tempTestimonials)
        .then(({ data }) => {
          if (!data.success) return;
          dispatch(SetUrlBuilderData(data.data));
          onClose();
        })
        .finally(() => setLoading(false));
    } catch (er) {}
  };

  return (
    <DialogWrapper
      open={isOpen}
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      title="Testimonial"
      onSubmit={handleSubmit(submit)}
      loading={loading}
    >
      <Grid item xs={12} className="mb-4">
        <Typography variant="subtitle2">Raiting</Typography>
        <Controller
          control={control}
          name="rating"
          render={({ field: { onChange, value } }) => (
            <Rating onChange={onChange} value={value} />
          )}
        />
        {errors.rating && (
          <FormHelperText variant="outlined" error>
            {errors.rating?.message}
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12} className="mb-4">
        <Typography variant="subtitle2">Comment</Typography>
        <Controller
          control={control}
          name="comment"
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={!!errors.comment}
              helperText={errors.comment?.message}
              fullWidth
            />
          )}
        />
      </Grid>
      <Grid item xs={12} className="mb-4">
        {imageToDisplay && (
          <div
            style={{ height: 70, width: 70, borderRadius: 40 }}
            className={`${circleButton.root} ${hoverStyle.root}`}
          >
            <Avatar
              style={{ height: "100%", width: "100%" }}
              src={imageToDisplay}
            />
            <HighlightOffOutlined
              onClick={() => removeImage()}
              className={hoverStyle.root_content}
              style={{ borderRadius: 40 }}
              color="error"
            />
          </div>
        )}
        {!imageToDisplay && (
          <>
            <input
              onChange={handleImageSelected}
              style={{ display: "none" }}
              accept="images/*"
              id="image-upload"
              type="file"
            />
            <label htmlFor="image-upload">
              <Typography variant="subtitle2">User Photo</Typography>
              <Fab
                style={{ height: 70, width: 70 }}
                classes={{ root: circleButton.root }}
                color="primary"
                aria-label="add"
                component="span"
              >
                <AddIcon />
              </Fab>
            </label>
          </>
        )}
      </Grid>
      <Grid item xs={12} className="mb-4">
        <Typography variant="subtitle2">Full Name</Typography>
        <Controller
          control={control}
          name="fullName"
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={!!errors.fullName}
              helperText={errors.fullName?.message}
              fullWidth
            />
          )}
        />
      </Grid>
      <Grid item xs={12} className="mb-4">
        <Typography variant="subtitle2">Title</Typography>
        <Controller
          control={control}
          name="title"
          render={({ field: { onChange, value } }) => (
            <TextField
              onChange={onChange}
              value={value}
              error={!!errors.title}
              helperText={errors.title?.message}
              fullWidth
            />
          )}
        />
      </Grid>
    </DialogWrapper>
  );
};
